import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const EditorActions = ({ artistId, artistData, onContentReload }) => {
  const [showAdvancedActions, setShowAdvancedActions] = useState(false);
  const [performing, setPerforming] = useState(null);

  const handleAction = async (action) => {
    setPerforming(action);
    try {
      switch (action) {
        case 'export-data':
          // Mock export functionality
          setTimeout(() => {
            const data = JSON.stringify({
              artist: artistData,
              timestamp: new Date()?.toISOString(),
              exportedBy: 'Admin Dashboard'
            }, null, 2);
            const blob = new Blob([data], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `artist-${artistData?.full_name?.replace(/\s+/g, '-')}-data.json`;
            a?.click();
            URL.revokeObjectURL(url);
          }, 1000);
          break;
        case 'refresh-content':
          await onContentReload?.();
          break;
        case 'generate-report':
          // Mock report generation
          setTimeout(() => {
            alert('Artist report generated and sent to admin email.');
          }, 1500);
          break;
        case 'bulk-approve':
          // Mock bulk approve
          setTimeout(() => {
            alert('All pending content has been approved.');
          }, 1000);
          break;
        case 'sync-mobile':
          // Mock mobile sync
          setTimeout(() => {
            alert('Artist content synchronized with mobile app.');
          }, 1200);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error performing action:', error);
      alert(`Failed to ${action}. Please try again.`);
    } finally {
      setPerforming(null);
    }
  };

  const mainActions = [
    {
      key: 'refresh-content',
      label: 'Refresh Content',
      icon: 'RefreshCw',
      description: 'Reload all content sections',
      variant: 'outline'
    },
    {
      key: 'export-data',
      label: 'Export Data',
      icon: 'Download',
      description: 'Download artist data as JSON',
      variant: 'outline'
    },
    {
      key: 'generate-report',
      label: 'Generate Report',
      icon: 'FileText',
      description: 'Create comprehensive artist report',
      variant: 'outline'
    }
  ];

  const advancedActions = [
    {
      key: 'bulk-approve',
      label: 'Bulk Approve Content',
      icon: 'CheckCheck',
      description: 'Approve all pending content items',
      variant: 'outline'
    },
    {
      key: 'sync-mobile',
      label: 'Sync Mobile App',
      icon: 'Smartphone',
      description: 'Force sync with mobile application',
      variant: 'outline'
    },
    {
      key: 'audit-log',
      label: 'View Audit Log',
      icon: 'ScrollText',
      description: 'View detailed change history',
      variant: 'ghost'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-text-primary">Administrative Actions</h3>
          <p className="text-sm text-text-secondary mt-1">
            Tools for managing artist content and account
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAdvancedActions(!showAdvancedActions)}
          iconName={showAdvancedActions ? 'ChevronUp' : 'ChevronDown'}
          iconPosition="right"
        >
          {showAdvancedActions ? 'Hide' : 'Show'} Advanced
        </Button>
      </div>

      {/* Main Actions */}
      <div className="flex flex-wrap gap-3">
        {mainActions?.map((action) => (
          <Button
            key={action?.key}
            variant={action?.variant}
            size="sm"
            onClick={() => handleAction(action?.key)}
            iconName={action?.icon}
            iconPosition="left"
            disabled={performing === action?.key}
            title={action?.description}
          >
            {performing === action?.key ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Processing...
              </span>
            ) : (
              action?.label
            )}
          </Button>
        ))}
      </div>

      {/* Advanced Actions */}
      {showAdvancedActions && (
        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-medium text-text-primary flex items-center gap-2">
            <Icon name="Settings" size={16} />
            Advanced Operations
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {advancedActions?.map((action) => (
              <button
                key={action?.key}
                onClick={() => handleAction(action?.key)}
                disabled={performing === action?.key}
                className="flex items-start gap-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                  {performing === action?.key ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name={action?.icon} size={16} className="text-text-secondary" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-primary">{action?.label}</p>
                  <p className="text-xs text-text-secondary mt-1">{action?.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div className="pt-4 border-t border-border">
        <h4 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <Icon name="Activity" size={16} />
          Recent Admin Actions
        </h4>
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between py-1">
            <span className="text-text-secondary">Last content refresh:</span>
            <span className="text-text-primary">2 minutes ago</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-text-secondary">Profile last updated:</span>
            <span className="text-text-primary">1 hour ago</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-text-secondary">Last admin view:</span>
            <span className="text-text-primary">Just now</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorActions;